// Building High-Quality Movers. Cert masterclass (Big Concept).
// Mux: TOratbvph02TUMzzwBEKa53SSaJyuq8kGg02Gk00jSA01l00
//
// Authored from the talk's transcript
// (knowledge-base/transcripts/cert/Building-Movers-Presentation.md).
// Quotes are lifted from what Coleman actually said. Bullets distill the
// practical takeaway. Coleman's voice, plain language, no em dashes.

export const BUILDING_MOVERS_MODULE_KEY = "building-high-quality-movers";

const VIDEO = "TOratbvph02TUMzzwBEKa53SSaJyuq8kGg02Gk00jSA01l00";

export const BUILDING_MOVERS_MODULE = {
  key: BUILDING_MOVERS_MODULE_KEY,
  title: "Building High-Quality Movers",
  sub: "Why the weird movement work in the warm up is not a gimmick, and how 10 minutes of it can build better athletes, better learners, and better hoopers.",
  coverVideoId: VIDEO,
  duration: "Full talk plus a guided breakdown",
  chapters: [
    {
      id: "watch",
      title: "Watch the talk",
      blocks: [
        { kind: "text", md:
          "This one does not fit neatly into a unit, but it is a concept worth understanding early. A lot of what Coleman is known for training wise is the movement based work: the crawling, the flipping, the weird stuff you have probably seen in the clips. This talk explains the why behind it. Watch it once start to finish, then come back through these sections for the ideas worth keeping." },
        { kind: "video", muxPlaybackId: VIDEO,
          caption: "Building High-Quality Movers." },
      ],
    },

    {
      id: "dont-waste-your-warmup",
      title: "1. Don't waste your warm up",
      blocks: [
        { kind: "text", md:
          "Almost all of this lives in the first 5 to 10 minutes of a session. That is the part most teams throw away. Coleman walked into a practice and the first thing he heard was the old one, two, three, four while everyone leaned over a hamstring stretch." },
        { kind: "pullquote", prominent: true, md:
          "I thought the days of static stretching were done. I understand it, it's tradition, it's worked for years. But cover some of these qualities in the warm up, literally 5 to 10 minutes a day, and it can change everything." },
        { kind: "text", md:
          "This is not an hour of flipping around in circles. It is a small, deliberate slice at the start of the day that changes a player from an engagement standpoint, a movement standpoint, and a lot more. The point of the talk is to show you what that slice is actually doing." },
        { kind: "callout", tone: "tip", title: "The frame to hold", body:
          "We still want most of the session to be basketball specific. We are hoopers. But this general movement work is the low hanging fruit almost nobody picks, and a small amount of it gives you a lot of bang for your buck." },
      ],
    },

    {
      id: "general-qualities",
      title: "2. Train the qualities, not just the play",
      blocks: [
        { kind: "text", md:
          "Most of the time we train the exact play, the exact move, the exact shot. That has its place. But sometimes training the qualities that feed the game is more bang for the buck, because one quality pays off across a whole range of situations." },
        { kind: "pullquote", md:
          "Rotational body control can apply to a spin move, to rotating in the air, to a fade away, to coming off a screen, even to rotating when you're on the ground. Cover the quality and you benefit a wide range of plays." },
        { kind: "text", md:
          "These qualities are often too minor to attack any other way. Run a contested finishing drill and you only get so many real reps of rotating. Isolate it in the warm up and a player can rotate 10 or 12 times in a single trip down the court. That is more efficient, and you are training something almost nobody thinks to train." },
        { kind: "text", md:
          "Think about other sports. Players say soccer helped them see passing angles or move laterally, gymnastics gave them body control, skiing built lateral shift. You could argue that time was stolen from basketball. Coleman argues the opposite: those players developed general qualities that pure specialists never build." },
        { kind: "callout", tone: "tip", title: "Examples of general qualities", body:
          "Rotational body control. Fluidity in low joint positions. Comfort with the ground (we will hit the floor, and a bad interaction can end a career). Aggression and competitiveness. Feel for closing and creating space. The list is basically endless. You cannot cover them all, and you do not need to." },
      ],
    },

    {
      id: "movement-library",
      title: "3. Add to the movement library",
      blocks: [
        { kind: "text", md:
          "The second reason is the movement library: the wider the range of movements a player is comfortable with, the better off they are. Coleman is certain about this from experience." },
        { kind: "pullquote", prominent: true, md:
          "Athletes with a wider range of movements learn easier, get injured less, and look more fluid." },
        { kind: "text", md:
          "We have all worked with the player where everything looks a little off. Ask for a between the legs and they can do it, but they are uncomfortable with it even when they are decent. And we have all worked with the player who can dance, who can play any sport you throw at them fluidly. That second player has a wide library, and that is why they pick up a new move or shot faster." },
        { kind: "principles", items: [
          { n: 1, title: "They learn easier", body:
            "A wide library means they have learned how to learn. They have repped the act of picking up motor skills, so the next new move comes quicker. The more you expose athletes to motor learning experiences, the better they get at learning itself." },
          { n: 2, title: "They get injured less", body:
            "They are prepared for anything. Whatever the game throws at them, they can adapt and figure it out because they are comfortable in more positions and more movements." },
          { n: 3, title: "They look more fluid", body:
            "They have experienced a lot, so anything that happens on the court they can make happen in a smooth, efficient way." },
        ]},
        { kind: "callout", tone: "tip", title: "How to actually add to it", body:
          "Find movements that athletes struggle with and that frustrate them a bit. For beginners that is easy: ask for a skip and they cannot do it, perfect. For advanced movers you have to dig: a different sport, a weird activity, even a dance off Instagram. The frustrating process of adding timing, rhythm, and coordination is exactly where the value is." },
      ],
    },

    {
      id: "fluidity-and-flow",
      title: "4. Help them find their flow",
      blocks: [
        { kind: "text", md:
          "This one is less tangible. There is a psychological barrier to moving fluidly and confidently. A lot of athletes could move better than they do, they just have not found their flow, and a big chunk of that barrier comes down by trying new movements." },
        { kind: "pullquote", md:
          "Young athletes must learn to move comfortably, fluidly, and confidently across a super broad range of possibilities and stops." },
        { kind: "text", md:
          "Players who specialized early or never played many sports are often tentative when they learn something new. The confidence is not there because the underlying fluidity is not there. Crawling, galloping, playing other sports: the more competent they become there, the more confident they are back on the court." },
        { kind: "text", md:
          "It also builds rhythm. Basketball is rhythmic, constantly switching between relaxing and contracting. A skip variation, a gallop, a footwork pattern with rhythm in it develops that better than just hoping it shows up through ball handling." },
      ],
    },

    {
      id: "oh-shit-reflex",
      title: "5. Hit the \"oh shit\" reflex",
      blocks: [
        { kind: "text", md:
          "The \"oh shit\" reflex is that feeling of losing balance, being in a weird position at speed, wondering if you are about to fall or get hurt. We have all felt it. Making it more familiar is a big step toward becoming a better athlete." },
        { kind: "pullquote", prominent: true, md:
          "When we hit this reflex, we're right at the edge of our capabilities, probably in a safe way. And that's where we want to be to become a better mover." },
        { kind: "text", md:
          "Watch the best ball handlers, the Kyrie Irvings and Darius Garlands. They get into positions where, if you were there, you would feel like you were at the absolute edge of what you could do. They thrive there. Kyrie spinning off his butt straight into a comfortable jumper is the picture of it." },
        { kind: "text", md:
          "It is hard to train this with a ball in your hands, so the easiest access is outside basketball. Movements that throw off coordination (a gallop they keep botching), a light push with eyes closed so they have to catch themselves, anything off balance, even a high box jump. Slight controlled danger is the point." },
        { kind: "callout", tone: "warn", title: "Keep it controlled", body:
          "Coleman only does the eyes-closed push with players he has worked with for years and trusts to fall safely. The push is light enough that nobody is hitting the ground hard. The goal is the feeling of \"oh shit, I have to catch myself,\" not actual risk." },
      ],
    },

    {
      id: "diversify-and-engage",
      title: "6. Diversify the training, make it engaging",
      blocks: [
        { kind: "text", md:
          "Almost no athlete has the Kobe build where they want nothing but reps, reps, reps. Volume is good, but diversifying with fun, engaging work a few times a week is more valuable than going at basketball for 10 more minutes." },
        { kind: "pullquote", md:
          "If I had to choose 10 minutes of general movement at the start of three sessions a week versus 10 more minutes of basketball, I'd choose the movement stuff every day. That 10 percent gets us more bang for our buck." },
        { kind: "text", md:
          "Basketball is inherently monotonous at some point for any dedicated player. The variety means less burnout and more longevity. Coleman knows real talent, potential pros, who quit after a college year because they built a bad relationship with the sport. Small sprinkles of this in the workout help prevent that." },
        { kind: "principles", items: [
          { n: 1, title: "Better state for learning", body:
            "Starting with something fun and engaging puts the nervous system in a more learning-friendly state. Some dopamine flowing, a better mood, and the memories you tag in that state are more favorable. The science backs that we absorb motor learning better this way." },
          { n: 2, title: "Physical relief from the same patterns", body:
            "Basketball hammers the same joints and movements over and over. That is why every hooper's knee hurts and why they end up with similar injuries and builds. Escaping the pattern even briefly helps prevent asymmetries." },
          { n: 3, title: "Fewer injuries, backed by data", body:
            "A study of NBA players found those who specialized in basketball in high school got injured significantly more than those who played multiple sports. Same structures, same movements, year after year." },
        ]},
      ],
    },

    {
      id: "awareness",
      title: "7. Build awareness, of the body and the space",
      blocks: [
        { kind: "text", md:
          "A lot of athletes simply do not feel what they are doing. You tell a player to lift the guide hand and it is buried down at their hip, and they swear it feels great. You show them the film and they say, oh wow, I didn't know I was doing that. That is missing internal awareness." },
        { kind: "pullquote", md:
          "You develop a greater feel for your own body by trying new, uncomfortable things. There are so many sensations we never get in basketball." },
        { kind: "text", md:
          "Exposing the nervous system to those new sensations creates better awareness, which creates better learners. Same story with spatial awareness. Coleman has had players who constantly step on the three point line or toe the sideline and swear they did not. After adding movement based work, their feel for their surroundings improved." },
      ],
    },

    {
      id: "donor-sports",
      title: "8. Donor sports",
      blocks: [
        { kind: "text", md:
          "A donor sport is the most practical way to apply all of this. It is not your main sport, but something that donates skills or qualities to it." },
        { kind: "compare",
          leftTitle: "The sport",
          leftItems: [
            "Soccer",
            "Skiing",
            "Tennis",
            "Swimming",
            "American football",
          ],
          rightTitle: "What it donates",
          rightItems: [
            "Footwork, seeing passing angles, patience",
            "Lateral movement",
            "Lateral movement, deceleration",
            "Fluidity",
            "Aggression, going up to catch, getting through contact",
          ],
        },
        { kind: "text", md:
          "You can find something for basically any sport. Coleman uses spike ball, pickle ball, a lot of different games. There is always a quality the athlete can carry back to the court, often in a more potent form than they could build on the court itself. Plus it is fun and breaks the monotony." },
        { kind: "checklist", id: "donor-sport-plan", title: "Putting donor sports to work", items: [
          "Young athletes (up to about 14): encourage them to fully play other sports, a soccer league, swimming, whatever diversifies their movement",
          "Older athletes: expose them here and there, soccer to warm up Saturday, pickle ball Thursday, spike ball the next day",
          "Pick the donor sport for the quality you want, not at random",
          "Let them connect it back: ask what they felt that they don't get on the court",
        ]},
      ],
    },

    {
      id: "breakdown",
      title: "9. The examples, broken down",
      blocks: [
        { kind: "text", md:
          "Coleman walks through the clips and names what each one is actually training. The pattern is the same every time: name the quality, name the benefit, and you can always explain it to the player." },
        { kind: "principles", items: [
          { n: 1, title: "Rotational pops and cartwheels", body:
            "Rotational body control, foot and ankle work popping off the ground, learning to control your whole body. Injury prevention from the weird landing angles, and getting over the initial fear so they find their flow." },
          { n: 2, title: "Carry races", body:
            "Functional strength sprinting without arms, plus a psychological challenge: every team finds a different way to do it. They screw up, they fall, they get back up. We are teaching them how to mess up and keep going." },
          { n: 3, title: "Tug of war and pushing", body:
            "Aggression and competitiveness isolated down to you against one person, plus learning to use the whole body as one unit to pull and leverage. Players even invent their own constraints to even it up." },
          { n: 4, title: "Backpedaling races and no-arm sprints", body:
            "Moving backwards is a general quality we should train and almost never do. The no-arm and constraint sprints are differential learning: vary the constraint so they can adapt their sprint form to anything." },
          { n: 5, title: "Yoga ball, box jumps, spike ball", body:
            "Embracing a self-chosen challenge and learning how to learn. Spike ball alone hits reaction, teamwork, low positions, change of direction, a new surface, and barefoot work." },
        ]},
        { kind: "pullquote", prominent: true, md:
          "These guys were not ready to do this. They were scared. But as they go they start to say, maybe I can do this. Now they're ready to jump into challenges, and they're more confident they can pick things up." },
      ],
    },

    {
      id: "selling-it",
      title: "10. Selling it, and the North Star",
      blocks: [
        { kind: "text", md:
          "Players will ask why they are doing this. That happens all the time. The answer is the same thing you just learned: for each activity, name the benefit and explain it. Once they see it, they flip from skeptical to bought in." },
        { kind: "pullquote", md:
          "Open the floodgates and tell them, just have fun with this, enjoy it, be like a kid. I've done this with pros, with beginners, and everyone in between, and it works." },
        { kind: "text", md:
          "It works from a psychological standpoint, an engagement standpoint, and a physical standpoint. People see results, they become more confident and more fluid movers, and they enjoy training more, which ultimately helps the business side too." },
        { kind: "callout", tone: "quote", title: "The North Star", body:
          "We still want most of the work to be basketball specific. This is roughly 10 percent of what we do, but that 10 percent builds better movers, which builds better learners, which builds better basketball players. It is an investment that pays back across everything." },
        { kind: "assess", id: "warmup-audit", prompt:
          "Honestly, how much of your warm up right now is building movers versus going through the motions of a static or monotonous routine?",
          lowLabel: "All going through the motions", highLabel: "All building movers" },
        { kind: "multipick", id: "next-warmups", prompt:
          "Which of these will you actually add to your warm up in the next few sessions?",
          options: [
            "A rotational or low-joint movement challenge",
            "A donor sport day (soccer, spike ball, pickle ball)",
            "An aggression or competitive battle (tug of war, pushing)",
            "A controlled \"oh shit\" balance challenge",
            "A differential-learning sprint with constraints",
          ]},
        { kind: "recap", id: "recap", title: "Your reflections from this masterclass" },
      ],
    },
  ],
};
